import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Save, 
  Package, 
  Plus, 
  Trash2, 
  FileText, 
  DollarSign, 
  Tag, 
  Calendar, 
  Truck, 
  User, 
  Wrench, 
  Building2, 
  AlertCircle, 
  Search, 
  Check,
  ShoppingCart,
  BarChart,
  Upload,
  QrCode
} from 'lucide-react';
import { productsService } from '../services/productsService';
import { exitsService } from '../services/exitsService';
import { imagesService } from '../services/imagesService';
import { trucksService } from '../services/trucksService';
import ImageUploader from './ImageUploader';

const ExitFormMulti = ({ onSave, onCancel, initialData = null, isEditMode = false }) => {
  const [exitData, setExitData] = useState({
    exit_date: new Date().toISOString().split('T')[0],
    requisition_number: '',
    delivered_to: '',
    truck: '',
    workshop: '',
    company: '',
    solicitante: '',
    reason: '',
    notes: '',
    kilometraje: '',
    requisition_image: null,
    requisition_image_url: null,
    products: []
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showImageUploader, setShowImageUploader] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  // Autocompletado de unidades (camiones)
  const [trucks, setTrucks] = useState([]);
  const [truckSuggestions, setTruckSuggestions] = useState([]);
  const [showTruckSuggestions, setShowTruckSuggestions] = useState(false);
  const truckInputRef = useRef(null);
  
  // Estados para escaneo de código de barras
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [lastKeypressTime, setLastKeypressTime] = useState(0);
  const [barcodeNotification, setBarcodeNotification] = useState({ 
    show: false, 
    message: '', 
    type: 'success',
    details: '' 
  });
  // Mapa para búsqueda rápida de productos por código
  const [productSearchMap, setProductSearchMap] = useState({});

  const productSearchRef = useRef(null);

  // Cargar datos iniciales si se está editando
  useEffect(() => {
    if (initialData) {
      let exitDate;
      if (initialData.exit_date) {
        // Crear una fecha con la zona horaria local
        const date = new Date(initialData.exit_date);
        // No ajustamos la zona horaria aquí para mantener la fecha como está en la BD
        exitDate = date.toISOString().split('T')[0];
      } else {
        // Si no hay fecha, usar la fecha actual
        const today = new Date();
        exitDate = today.toISOString().split('T')[0];
      }

      setExitData({
        ...initialData,
        exit_date: exitDate,
        products: Array.isArray(initialData.products) ? initialData.products.map(p => ({
          product_id: p.product_id,
          quantity: p.quantity,
          unit_cost: p.unit_cost,
          total_cost: p.total_cost,
          product_name: p.product_name,
          product_sku: p.product_sku,
          product_unit: p.product_unit,
          product_image: p.product_image
        })) : []
      });
      
      if (Array.isArray(initialData.products)) {
        setSelectedProducts(initialData.products.map(p => ({
          id: p.product_id,
          name: p.product_name,
          sku: p.product_sku,
          unit: p.product_unit,
          quantity: p.quantity,
          unit_cost: p.unit_cost,
          total_cost: p.total_cost,
          image: p.product_image
        })));
      }
    }
  }, [initialData]);

  // Cargar lista de productos
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Usamos getAllUnpaginated que ya devuelve directamente el array de productos
        const productsData = await productsService.getAllUnpaginated();
        
        if (Array.isArray(productsData)) {
          const productsWithStock = productsData.filter(p => p.current_stock > 0);
          setAllProducts(productsWithStock);
          setFilteredProducts(productsWithStock);
          
          // Crear un mapa de búsqueda para códigos de barras
          const searchMap = {};
          (productsWithStock || []).forEach(product => {
            // Indexar por barcode (código de barras)
            if (product.barcode) {
              const normalizedBarcode = product.barcode.trim().toLowerCase();
              searchMap[normalizedBarcode] = product;
              
              // Versión sin caracteres especiales
              const cleanBarcode = normalizedBarcode.replace(/[^a-z0-9]/g, '');
              if (cleanBarcode && cleanBarcode.length > 3) {
                searchMap[cleanBarcode] = product;
              }
              
              // Versión con guiones en lugar de comillas
              const dashedBarcode = normalizedBarcode.replace(/'/g, '-');
              if (dashedBarcode !== normalizedBarcode) {
                searchMap[dashedBarcode] = product;
              }
            }
            
            // Indexar por SKU
            if (product.sku) {
              const normalizedSku = product.sku.trim().toLowerCase();
              searchMap[normalizedSku] = product;
              
              // Versión sin caracteres especiales
              const cleanSku = normalizedSku.replace(/[^a-z0-9]/g, '');
              if (cleanSku && cleanSku.length > 3) {
                searchMap[cleanSku] = product;
              }
            }
            
            // Indexar por code (código interno)
            if (product.code) {
              const normalizedCode = product.code.trim().toLowerCase();
              searchMap[normalizedCode] = product;
              
              // Versión sin caracteres especiales
              const cleanCode = normalizedCode.replace(/[^a-z0-9]/g, '');
              if (cleanCode && cleanCode.length > 3) {
                searchMap[cleanCode] = product;
              }
            }
          });
          
          console.log('🔍 Mapa de búsqueda creado con', Object.keys(searchMap).length, 'entradas');
          setProductSearchMap(searchMap);
        } else {
          console.warn('Formato de datos de productos inesperado:', productsData);
          setAllProducts([]);
          setFilteredProducts([]);
        }
      } catch (error) {
        console.error('Error cargando productos:', error);
        // Si hay un error, inicializar con arrays vacíos para evitar problemas
        setAllProducts([]);
        setFilteredProducts([]);
      }
    };

    loadProducts();
    // Cargar lista de unidades para autocompletado
    const loadTrucks = async () => {
      try {
        const resp = await trucksService.getAll({ limit: 500 });
        if (resp?.data && Array.isArray(resp.data)) {
          setTrucks(resp.data);
        }
      } catch (e) {
        console.warn('No se pudieron cargar unidades para autocompletado:', e.message);
      }
    };
    loadTrucks();
  }, []);

  // Filtrar productos según búsqueda
  useEffect(() => {
    if (productSearchTerm.trim() === '') {
      setFilteredProducts(allProducts);
    } else {
      const term = productSearchTerm.toLowerCase();
      setFilteredProducts(allProducts.filter(product => 
        (product.name?.toLowerCase() || '').includes(term) || 
        (product.sku?.toLowerCase() || '').includes(term) ||
        (product.category?.toLowerCase() || '').includes(term) ||
        // Alternativas para diferentes estructuras de datos
        (product.product_name?.toLowerCase() || '').includes(term) ||
        (product.product_sku?.toLowerCase() || '').includes(term)
      ));
    }
  }, [productSearchTerm, allProducts]);

  // Registrar y desregistrar evento de escucha de teclado para el escáner de códigos de barras
  useEffect(() => {
    // Función para capturar eventos de teclado en todo el documento
    const handleGlobalKeyDown = (event) => {
      // Solo procesar si no estamos en un campo de entrada
      const activeElement = document.activeElement;
      const isInputActive = activeElement.tagName === 'INPUT' || 
                          activeElement.tagName === 'TEXTAREA' || 
                          activeElement.isContentEditable;
      
      // Si estamos en un campo de entrada y no es un Enter, permitir el comportamiento normal
      if (isInputActive && event.key !== 'Enter') {
        return;
      }
      
      // Si se presiona Enter en un campo de entrada, primero procesamos normalmente
      // pero también queremos capturarlo para el código de barras
      if (isInputActive && event.key === 'Enter') {
        // Permitir que el evento se propague para su comportamiento normal
        // pero también lo procesamos para el código de barras
        handleKeyDown(event);
        return;
      }
      
      // Para cualquier otra tecla fuera de campos de entrada, procesarla para el código de barras
      handleKeyDown(event);
    };
    
    // Registrar el evento
    document.addEventListener('keydown', handleGlobalKeyDown);
    
    // Limpieza al desmontar
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [barcodeBuffer, lastKeypressTime]); // Dependencias necesarias para que el efecto se actualice

  // Función para normalizar códigos específicos
  const normalizeSpecificCodes = (code) => {
    // Lista de patrones conocidos y sus normalizaciones
    const knownPatterns = [
      // Formatos de aceites y lubricantes que pueden contener comillas
      { pattern: /ACE'(\d+)W(\d+)/i, replacement: 'ACE-$1W$2' }, // Ejemplo: ACE'10W40 -> ACE-10W40
      { pattern: /ACE'(\d+[A-Z]\d+)/i, replacement: 'ACE-$1' }, // Más genérico para otros formatos de ACE
      { pattern: /'(\d+W\d+)/i, replacement: '-$1' }, // Ejemplo: '10W40 -> -10W40
      
      // Patrones genéricos para manejar códigos con formato [texto]'[números/letras]
      { pattern: /(\w+)'(\w+)/g, replacement: '$1-$2' }, // Patrón general: TEXTO'TEXTO -> TEXTO-TEXTO
      
  // Reemplazar comillas simples por guiones (caso común en escáneres)
  { pattern: /'([A-Za-z0-9]+)'/g, replacement: '-$1-' }, // Ejemplo: 'ABC123' -> -ABC123-
  { pattern: /'([A-Za-z0-9]+)/g, replacement: '-$1' },   // Ejemplo: 'ABC123 -> -ABC123
  { pattern: /([A-Za-z0-9]+)'/g, replacement: '$1-' },   // Ejemplo: ABC123' -> ABC123-
      
  // Manejar comillas simples sueltas
  { pattern: /'/g, replacement: '-' },  // Reemplazar todas las comillas simples por guiones
  // Manejar comas que algunos escáneres insertan en lugar de guiones
  { pattern: /,/g, replacement: '-' },  // Reemplazar comas por guiones
      
      // Limpiar guiones múltiples consecutivos que pudieran generarse
      { pattern: /-{2,}/g, replacement: '-' }, // Ejemplo: --- -> -
      
      // Quitar espacios excesivos que pudieran confundir la búsqueda
      { pattern: /\s+/g, replacement: ' ' },
    ];
    
    // Aplicar cada patrón de normalización
    let normalizedCode = code;
    knownPatterns.forEach(({ pattern, replacement }) => {
      normalizedCode = normalizedCode.replace(pattern, replacement);
    });
    
    return normalizedCode;
  };
  
  // Función para procesar el código de barras completo
  const processBarcodeInput = () => {
    // Constantes para configuración
    const MIN_BARCODE_LENGTH = 4;
    
    // Lista de teclas especiales que no deberían considerarse como códigos de barras válidos
    const specialKeys = ['Backspace', 'Delete', 'Enter', 'Tab', 'Escape', 'Home', 'End', 
                        'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
                        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'];
    
    // Verificar si el buffer contiene solo una tecla especial (rápida verificación)
    if (specialKeys.includes(barcodeBuffer)) {
      console.log('🔄 Ignorando tecla especial:', barcodeBuffer);
      setBarcodeBuffer('');
      return;
    }
    
    if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
      // Tiempo de inicio para medir rendimiento
      const startTime = performance.now();
      
      // Limpiamos el código de barras para evitar caracteres problemáticos
      const sanitizedBarcode = barcodeBuffer.replace(/['"\\]/g, ''); // Eliminar comillas y barras invertidas
      
      // Aplicar normalización específica para formatos conocidos
      const normalizedBarcode = normalizeSpecificCodes(barcodeBuffer);
      
      console.log('🔍 Código de barras escaneado (original):', barcodeBuffer);
      
      // Buscar primero en el mapa de búsqueda rápida (esto es más rápido)
      let foundProduct = null;
        const searchKeys = [
        barcodeBuffer.trim().toLowerCase(),
        sanitizedBarcode.trim().toLowerCase(),
        normalizedBarcode.trim().toLowerCase(),
        barcodeBuffer.replace(/'/g, '-').trim().toLowerCase(),
          barcodeBuffer.replace(/,/g, '-').trim().toLowerCase(),
        barcodeBuffer.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase(),
        sanitizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase(),
        normalizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase()
      ];
      
      // Intentar encontrar el producto en el mapa de búsqueda (más rápido)
      for (const key of searchKeys) {
        if (productSearchMap[key]) {
          foundProduct = productSearchMap[key];
          console.log('✅ Producto encontrado rápidamente por clave:', key);
          break;
        }
      }
      
      // Si no se encontró en el mapa, intentar el método tradicional
      if (!foundProduct) {
        // Crear un array de posibles formatos para el método tradicional
        const possibleCodes = [
          sanitizedBarcode.trim(),
          normalizedBarcode.trim(),
          barcodeBuffer.trim(),
          barcodeBuffer.replace(/'/g, '-').trim(),
          barcodeBuffer.replace(/[^a-zA-Z0-9]/g, '').trim(),
          sanitizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim(),
          normalizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim()
        ];
        
        foundProduct = allProducts.find(product => {
          // Obtener los códigos del producto y normalizarlos
          const productBarcode = product.barcode ? product.barcode.trim() : '';
          const productSku = product.sku ? product.sku.trim() : '';
          const productCode = product.code ? product.code.trim() : '';
          
          // También normalizar los códigos del producto para mayor compatibilidad
          const normalizedBarcode = productBarcode ? normalizeSpecificCodes(productBarcode) : '';
          const normalizedSku = productSku ? normalizeSpecificCodes(productSku) : '';
          const normalizedCode = productCode ? normalizeSpecificCodes(productCode) : '';
          
          // Crear un array con todos los posibles códigos del producto
          const productCodes = [
            productBarcode, productSku, productCode,
            normalizedBarcode, normalizedSku, normalizedCode,
            // Versiones en minúsculas
            productBarcode.toLowerCase(), productSku.toLowerCase(), productCode.toLowerCase(),
            normalizedBarcode.toLowerCase(), normalizedSku.toLowerCase(), normalizedCode.toLowerCase(),
            // Versiones sin caracteres especiales
            productBarcode.replace(/[^a-zA-Z0-9]/g, ''),
            productSku.replace(/[^a-zA-Z0-9]/g, ''),
            productCode.replace(/[^a-zA-Z0-9]/g, '')
          ].filter(code => code !== ''); // Eliminar valores vacíos
          
          // Verificar coincidencias entre los códigos del producto y los posibles formatos del código escaneado
          return possibleCodes.some(scannedCode => {
            if (!scannedCode) return false;
            
            // Comprobar coincidencia exacta o con normalización
            return productCodes.some(productCode => {
              if (!productCode) return false;
              
              // Comprobar coincidencia exacta
              if (productCode === scannedCode) return true;
              
              // Comprobar si el código escaneado está contenido en el código del producto (para casos parciales)
              if (productCode.includes(scannedCode) && scannedCode.length >= 5) return true;
              
              // Comprobar si el código del producto está contenido en el código escaneado (para casos con prefijos/sufijos)
              if (scannedCode.includes(productCode) && productCode.length >= 5) return true;
              
              return false;
            });
          });
        });
      }
      
      // Medir el tiempo que tomó la búsqueda
      const endTime = performance.now();
      const searchTime = (endTime - startTime).toFixed(2);
      console.log(`🕒 Búsqueda completada en ${searchTime}ms`);
      
      if (foundProduct) {
        console.log('✅ Producto encontrado:', foundProduct);
        
        // Verificar si el producto ya está en la lista
        const isProductAlreadyAdded = selectedProducts.some(p => p.id === foundProduct.id);
        
        if (isProductAlreadyAdded) {
          // Incrementar la cantidad si el producto ya está en la lista
          setSelectedProducts(prev => prev.map(p => 
            p.id === foundProduct.id 
              ? { ...p, quantity: p.quantity + 1, total_cost: (p.quantity + 1) * p.unit_cost } 
              : p
          ));
          
          // También actualizar exitData.products
          setExitData(prev => ({
            ...prev,
            products: prev.products.map(p => 
              p.product_id === foundProduct.id 
                ? { ...p, quantity: p.quantity + 1, total_cost: (p.quantity + 1) * p.unit_cost } 
                : p
            )
          }));
          
          // Mostrar notificación de incremento
          setBarcodeNotification({
            show: true,
            message: `Cantidad incrementada: ${foundProduct.name}`,
            type: 'success',
            details: `SKU: ${foundProduct.sku || ''} | Nuevo total: ${
              selectedProducts.find(p => p.id === foundProduct.id)?.quantity + 1
            }`
          });
        } else {
          // Añadir el producto a la lista
          // Usar el costo promedio (average_cost) en lugar del precio (price)
          const unitCost = foundProduct.average_cost || foundProduct.price || 0;
          
          const newProduct = {
            id: foundProduct.id,
            name: foundProduct.name,
            sku: foundProduct.sku,
            unit: foundProduct.unit || 'pzs',
            quantity: 1,
            unit_cost: unitCost,
            total_cost: unitCost,
            image: foundProduct.image
          };
          
          setSelectedProducts(prev => [...prev, newProduct]);
          
          // También actualizar exitData.products
          setExitData(prev => ({
            ...prev,
            products: [
              ...prev.products,
              {
                product_id: foundProduct.id,
                product_name: foundProduct.name,
                product_sku: foundProduct.sku,
                product_unit: foundProduct.unit || 'pzs',
                quantity: 1,
                unit_cost: unitCost,
                total_cost: unitCost
              }
            ]
          }));
          
          // Mostrar notificación de éxito
          setBarcodeNotification({
            show: true,
            message: `Producto agregado: ${foundProduct.name}`,
            type: 'success',
            details: `SKU: ${foundProduct.sku || ''} | Código: ${foundProduct.code || ''}`
          });
        }
        
        // Limpiar la notificación después de un momento
        setTimeout(() => {
          setBarcodeNotification({ show: false, message: '', type: 'success', details: '' });
        }, 3000);
      } else {
        // Construir mensaje con información útil para depuración
        console.log('❌ No se encontró ningún producto:', 
          'Original:', JSON.stringify(barcodeBuffer), 
          'Sanitizado:', JSON.stringify(sanitizedBarcode), 
          'Normalizado:', JSON.stringify(normalizedBarcode));
        
        // Mostrar notificación de error con información útil
        setBarcodeNotification({
          show: true,
          message: `No se encontró producto con código: ${normalizedBarcode}`,
          type: 'error',
          details: `Prueba buscar manualmente con: ${barcodeBuffer}`
        });
        
        // Ocultar la notificación después de un tiempo
        setTimeout(() => {
          setBarcodeNotification({ show: false, message: '', type: 'error', details: '' });
        }, 3000);
      }
    } else if (barcodeBuffer.length > 0) {
      console.log('⚠️ Código demasiado corto, ignorando:', barcodeBuffer);
      
      // Mostrar notificación de código inválido
      setBarcodeNotification({
        show: true,
        message: 'Código demasiado corto para ser válido',
        type: 'warning'
      });
      
      setTimeout(() => {
        setBarcodeNotification(prev => ({ ...prev, show: false }));
      }, 2000);
    }
    
    // Limpiar el buffer para la siguiente lectura
    setBarcodeBuffer('');
  };
  
  // Función para manejar las pulsaciones de teclas (para el escaneo de códigos de barras)
  const handleKeyDown = (event) => {
    // Constantes para configuración
    const MAX_SCAN_GAP = 50; // Tiempo máximo entre pulsaciones para considerarlas parte del mismo escaneo (ms)
    const SCAN_TIMEOUT = 300; // Tiempo máximo para considerar un escaneo completo (ms)
    const MIN_BARCODE_LENGTH = 4; // Longitud mínima del código para considerarlo un código de barras válido
    
    // Ignorar eventos de teclas modificadoras, navegación y teclas especiales
    if (event.ctrlKey || event.altKey || event.metaKey) return;
    if (event.key === 'Shift' || event.key === 'Control' || event.key === 'Alt' || event.key === 'Meta') return;
    if (event.key === 'Tab' || event.key === 'Escape' || event.key === 'ArrowUp' || 
        event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;
        
    // Ignorar la tecla Backspace y otras teclas de control para evitar procesarlas como códigos de barras
    if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Home' || 
        event.key === 'End' || event.key === 'PageUp' || event.key === 'PageDown' ||
        event.key === 'F1' || event.key === 'F2' || event.key === 'F3' || event.key === 'F4' ||
        event.key === 'F5' || event.key === 'F6' || event.key === 'F7' || event.key === 'F8' ||
        event.key === 'F9' || event.key === 'F10' || event.key === 'F11' || event.key === 'F12') {
      // Solo permitir que Backspace borre caracteres del buffer si hay contenido
      if (event.key === 'Backspace' && barcodeBuffer.length > 0) {
        setBarcodeBuffer(prevBuffer => prevBuffer.slice(0, -1));
        setLastKeypressTime(new Date().getTime());
      }
      return;
    }
    
    const currentTime = new Date().getTime();
    const timeSinceLastKeypress = currentTime - lastKeypressTime;
    
    // Los escáneres de código de barras generalmente envían caracteres muy rápidamente
    if (timeSinceLastKeypress > MAX_SCAN_GAP && barcodeBuffer.length > 0) {
      // Si ha pasado más tiempo, es una nueva lectura o interacción manual
      // Solo resetear si ya teníamos algo en el buffer (podría ser una entrada manual lenta)
      if (timeSinceLastKeypress > SCAN_TIMEOUT) {
        setBarcodeBuffer('');
      }
    }
    
    // Procesar el caracter
    if (event.key === 'Enter') {
      // El Enter típicamente señala el final de un escaneo de código de barras
      event.preventDefault(); // Prevenir la acción predeterminada para no enviar formularios
      
      if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
        processBarcodeInput();
      } else if (barcodeBuffer.length > 0) {
        console.log('⚠️ Código demasiado corto, ignorando:', barcodeBuffer);
        setBarcodeBuffer('');
        
        // Mostrar notificación de código inválido
        setBarcodeNotification({
          show: true,
          message: 'Código demasiado corto para ser válido',
          type: 'warning'
        });
        
        setTimeout(() => {
          setBarcodeNotification(prev => ({ ...prev, show: false }));
        }, 2000);
      }
    } else {
      // Acumular el caracter en el buffer usando try-catch para manejar caracteres problemáticos
      try {
        setBarcodeBuffer(prevBuffer => prevBuffer + event.key);
      } catch (error) {
        console.error('Error al añadir caracter al buffer:', error);
        // Si hay un error, intentamos añadir un valor seguro
        setBarcodeBuffer(prevBuffer => prevBuffer + '_');
      }
    }
    
    setLastKeypressTime(currentTime);
  };

  // Formatear dinero
  const formatMoney = (amount) => {
    if (!amount) return '$0.00 MXN';
    
    const number = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(number)) return '$0.00 MXN';
    
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(number);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setExitData(prev => ({
      ...prev,
      [name]: value
    }));

    // Autocompletado para campo Camión
    if (name === 'truck') {
      const term = value.trim().toLowerCase();
      if (term.length >= 2) {
        const matches = trucks
          .filter(t => (t.numero_economico || '').toLowerCase().includes(term))
          .slice(0, 8);
        setTruckSuggestions(matches);
        setShowTruckSuggestions(matches.length > 0);
      } else {
        setTruckSuggestions([]);
        setShowTruckSuggestions(false);
      }
    }
    
    // Limpiar errores al cambiar un campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectTruck = (numeroEconomico) => {
    setExitData(prev => ({ ...prev, truck: numeroEconomico }));
    setShowTruckSuggestions(false);
  };

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (truckInputRef.current && !truckInputRef.current.contains(event.target)) {
        setShowTruckSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProductSelect = (product) => {
    // Verificar si el producto ya está seleccionado
    const alreadySelected = selectedProducts.some(p => p.id === product.id);
    
    if (!alreadySelected) {
      // Usar el costo promedio para el cálculo
      const unitCost = product.average_cost || product.price || 0;
      
      const newSelectedProduct = {
        ...product,
        quantity: 1,
        unit_cost: unitCost,
        total_cost: unitCost
      };
      
      setSelectedProducts(prev => [...prev, newSelectedProduct]);
      
      // También actualizar exitData.products
      setExitData(prev => ({
        ...prev,
        products: [
          ...prev.products,
          {
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku,
            product_unit: product.unit || 'pzs',
            quantity: 1,
            unit_cost: unitCost,
            total_cost: unitCost
          }
        ]
      }));
    }
    
    setShowProductSelector(false);
    setProductSearchTerm('');
  };

  const handleProductQuantityChange = (index, quantity) => {
    const updatedProducts = [...selectedProducts];
    const product = updatedProducts[index];
    
    quantity = Math.max(1, parseInt(quantity) || 0); // Asegurar que sea al menos 1
    
    // No permitir cantidad mayor al stock disponible
    const maxStock = allProducts.find(p => p.id === product.id)?.current_stock || 0;
    quantity = Math.min(quantity, maxStock);
    
    product.quantity = quantity;
    product.total_cost = product.unit_cost * quantity;
    
    setSelectedProducts(updatedProducts);
    
    // Actualizar exitData
    const exitProducts = [...exitData.products];
    exitProducts[index] = {
      ...exitProducts[index],
      quantity,
      total_cost: product.unit_cost * quantity
    };
    
    setExitData(prev => ({
      ...prev,
      products: exitProducts
    }));
  };

  const handleProductCostChange = (index, cost) => {
    const updatedProducts = [...selectedProducts];
    const product = updatedProducts[index];
    
    cost = Math.max(0, parseFloat(cost) || 0);
    
    product.unit_cost = cost;
    product.total_cost = cost * product.quantity;
    
    setSelectedProducts(updatedProducts);
    
    // Actualizar exitData
    const exitProducts = [...exitData.products];
    exitProducts[index] = {
      ...exitProducts[index],
      unit_cost: cost,
      total_cost: cost * product.quantity
    };
    
    setExitData(prev => ({
      ...prev,
      products: exitProducts
    }));
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...selectedProducts];
    updatedProducts.splice(index, 1);
    setSelectedProducts(updatedProducts);
    
    const exitProducts = [...exitData.products];
    exitProducts.splice(index, 1);
    
    setExitData(prev => ({
      ...prev,
      products: exitProducts
    }));
  };

  const handleImageUpload = (imageFileName) => {
    // Asegurarnos de que recibimos el nombre del archivo correctamente
    if (!imageFileName) {
      console.log('No se recibió un nombre de archivo válido');
      setExitData(prev => ({
        ...prev,
        requisition_image: null
      }));
    } else {
      // Construir la URL completa para mostrar la imagen correctamente
      const imageUrl = imagesService.getImageUrl(imageFileName);
      console.log('URL de imagen construida:', imageUrl);
      
      setExitData(prev => ({
        ...prev,
        requisition_image: imageFileName,
        requisition_image_url: imageUrl // Guardar también la URL completa
      }));
    }
    
    setShowImageUploader(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validaciones básicas
    if (!exitData.exit_date) {
      newErrors.exit_date = 'La fecha de salida es requerida';
    }
    
    if (selectedProducts.length === 0) {
      newErrors.products = 'Debe seleccionar al menos un producto';
    } else {
      // Verificar que todos los productos tengan cantidad válida
      const invalidProducts = selectedProducts.filter(p => !p.quantity || p.quantity <= 0);
      if (invalidProducts.length > 0) {
        newErrors.products = 'Todos los productos deben tener una cantidad válida';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotalCost = () => {
    return selectedProducts.reduce((total, product) => total + (product.total_cost || 0), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      let result;
      
      // Asegurarnos de que los datos requeridos estén presentes
      if (!exitData.exit_date) {
        throw new Error('La fecha de salida es requerida');
      }
      
      if (!selectedProducts || selectedProducts.length === 0) {
        throw new Error('Debe seleccionar al menos un producto');
      }
      
      // Crear una fecha con la zona horaria local para evitar el problema de -1 día
      let localExitDate = new Date(exitData.exit_date);
      // Ajustar para la zona horaria
      localExitDate.setMinutes(localExitDate.getMinutes() + localExitDate.getTimezoneOffset());
      // Formatear la fecha correctamente (YYYY-MM-DD)
      const formattedDate = localExitDate.toISOString().split('T')[0];
      
      // Preparar datos para enviar - formato para multi-productos
      const formData = {
        exit_date: formattedDate, // Usar la fecha corregida
        requisition_number: exitData.requisition_number || '',
        delivered_to: exitData.delivered_to || '',
        truck: exitData.truck || '',
        workshop: exitData.workshop || '',
        company: exitData.company || '',
        solicitante: exitData.solicitante || '',
        reason: exitData.reason || '',
        notes: exitData.notes || '',
        kilometraje: exitData.kilometraje !== '' ? parseInt(exitData.kilometraje) : null,
        requisition_image: exitData.requisition_image || null,
        products: selectedProducts.map(p => {
          // Validar que el producto tenga todos los campos requeridos
          const productId = parseInt(p.id);
          const quantity = parseFloat(p.quantity);
          const unitCost = parseFloat(p.unit_cost);
          const totalCost = parseFloat(p.total_cost);
          
          // Log para debugging si hay algún valor inválido
          if (!productId || isNaN(quantity) || isNaN(unitCost)) {
            console.error('❌ Producto con datos inválidos:', {
              original: p,
              parsed: { productId, quantity, unitCost, totalCost }
            });
          }
          
          return {
            product_id: productId,
            quantity: isNaN(quantity) ? 0 : quantity,
            unit_cost: isNaN(unitCost) ? 0 : unitCost,
            total_cost: isNaN(totalCost) ? 0 : totalCost
          };
        }).filter(p => p.product_id && p.quantity > 0) // Filtrar productos inválidos
      };
      
      // IMPORTANTE: No incluir product_id, quantity, unit_cost, total_cost a nivel raíz
      // Estos campos solo funcionan con el backend para salidas de un solo producto
      // Para salidas multi-producto, solo debe enviarse el array "products"
      
      console.log('Enviando datos de salida:', JSON.stringify(formData, null, 2));
      
      if (isEditMode && initialData?.id) {
        // Modo edición
        result = await exitsService.update(initialData.id, formData);
      } else {
        // Modo creación
        result = await exitsService.create(formData);
      }
      
      console.log('Respuesta del servidor:', result);
      
      // Mostrar mensaje de éxito
      setShowSuccessMessage(true);
      setSuccessMessage(isEditMode ? '¡Salida actualizada exitosamente!' : '¡Salida registrada exitosamente!');
      
      // Esperar 1.5 segundos antes de cerrar y hacer callback
      setTimeout(() => {
        setShowSuccessMessage(false);
        
        // Callback con el resultado
        if (onSave) {
          onSave(result);
        }
      }, 1500);
    } catch (error) {
      console.error('Error guardando salida:', error);
      
      // Mejorar el mensaje de error para que sea más informativo
      let errorMessage = 'Error guardando los datos. Intente nuevamente.';
      
      if (error.message) {
        errorMessage = error.message;
      }
      
      if (error.response && error.response.data) {
        console.error('Detalles del error del servidor:', error.response.data);
        errorMessage = error.response.data.error || error.response.data.message || errorMessage;
      }
      
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && selectedProducts.length === 0) {
      setErrors({ products: 'Debe seleccionar al menos un producto' });
      return;
    }
    
    setCurrentStep(prev => prev + 1);
    setErrors({});
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full mx-4 my-8 max-h-[90vh] overflow-hidden"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex justify-between items-center bg-purple-600 text-white p-5">
          <h2 className="text-2xl font-bold">
            {isEditMode ? 'Editar Salida' : 'Registrar Nueva Salida'}
          </h2>
          <motion.button
            className="p-2 rounded-full hover:bg-purple-700 transition-colors"
            onClick={onCancel}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="w-6 h-6" />
          </motion.button>
        </div>

        {/* Mensaje de éxito */}
        <AnimatePresence>
          {showSuccessMessage && (
            <motion.div
              className="bg-green-500 text-white px-6 py-4 flex items-center justify-center gap-3"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Check className="w-6 h-6" />
              <span className="text-lg font-semibold">{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-5rem)]">
          {/* Pasos del formulario */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div 
                className={`flex items-center ${currentStep >= 1 ? 'text-purple-600' : 'text-gray-400'}`}
              >
                <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                  currentStep >= 1 ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  <Package className="w-5 h-5" />
                </div>
                <span className="ml-2 font-medium">Productos</span>
              </div>
              <div className={`flex-1 h-0.5 mx-4 ${currentStep >= 2 ? 'bg-purple-400' : 'bg-gray-200'}`}></div>
              <div 
                className={`flex items-center ${currentStep >= 2 ? 'text-purple-600' : 'text-gray-400'}`}
              >
                <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                  currentStep >= 2 ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  <FileText className="w-5 h-5" />
                </div>
                <span className="ml-2 font-medium">Información</span>
              </div>
              <div className={`flex-1 h-0.5 mx-4 ${currentStep >= 3 ? 'bg-purple-400' : 'bg-gray-200'}`}></div>
              <div 
                className={`flex items-center ${currentStep >= 3 ? 'text-purple-600' : 'text-gray-400'}`}
              >
                <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                  currentStep >= 3 ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400'
                }`}>
                  <Check className="w-5 h-5" />
                </div>
                <span className="ml-2 font-medium">Confirmación</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <AnimatePresence mode="wait">
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                      <Package className="w-5 h-5 mr-2 text-purple-600" />
                      Selección de Productos
                    </h3>
                    
                    {errors.products && (
                      <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        {errors.products}
                      </div>
                    )}
                    
                    <div className="bg-purple-50 p-4 rounded-lg mb-4">
                      <div className="flex items-center gap-3 mb-3">
                        <ShoppingCart className="w-5 h-5 text-purple-600" />
                        <span className="font-medium text-purple-800">
                          Productos Seleccionados: {selectedProducts.length}
                        </span>
                      </div>
                      
                      <motion.button
                        type="button"
                        onClick={() => setShowProductSelector(true)}
                        className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white p-3 rounded-lg hover:bg-purple-700 transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Plus className="w-5 h-5" />
                        Agregar Producto
                      </motion.button>
                      
                      {/* Indicador de escaneo de código de barras */}
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-3">
                        <QrCode className="w-5 h-5 text-blue-600" />
                        <div className="flex-1">
                          <p className="text-sm text-blue-700 font-medium">
                            Escáner de Código de Barras Activo
                          </p>
                          <p className="text-xs text-blue-600">
                            Escanee cualquier producto con un lector de códigos para agregarlo
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {selectedProducts.length > 0 ? (
                      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Producto
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Cantidad
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Costo Unitario
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Costo Total
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Acciones
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedProducts.map((product, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                                      {product.image ? (
                                        <img 
                                          src={product.image} 
                                          alt={product.name} 
                                          className="h-10 w-10 rounded-lg object-cover"
                                        />
                                      ) : (
                                        <Package className="h-5 w-5 text-gray-400" />
                                      )}
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                      <div className="text-xs text-gray-500">{product.sku}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center justify-center">
                                    <input
                                      type="number"
                                      min="1"
                                      value={product.quantity}
                                      onChange={(e) => handleProductQuantityChange(index, e.target.value)}
                                      className="w-20 px-2 py-1 text-center border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                                    />
                                    <span className="ml-2 text-sm text-gray-500">{product.unit || 'pzs'}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <div className="flex items-center justify-center">
                                    <span className="text-gray-500 mr-1">$</span>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={product.unit_cost}
                                      onChange={(e) => handleProductCostChange(index, e.target.value)}
                                      className="w-24 px-2 py-1 text-center border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                                    />
                                  </div>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatMoney(product.total_cost)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-center">
                                  <motion.button
                                    type="button"
                                    onClick={() => handleRemoveProduct(index)}
                                    className="text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50"
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </motion.button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-50">
                            <tr>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                Total
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                                {selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} productos
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap"></td>
                              <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-bold text-purple-700">
                                {formatMoney(calculateTotalCost())}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap"></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                        <Package className="w-12 h-12 text-gray-400 mx-auto" />
                        <p className="mt-2 text-gray-500">
                          No hay productos seleccionados
                        </p>
                        <button
                          type="button"
                          onClick={() => setShowProductSelector(true)}
                          className="mt-4 inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Seleccionar Productos
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end mt-6">
                    <motion.button
                      type="button"
                      onClick={nextStep}
                      className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      disabled={selectedProducts.length === 0}
                    >
                      Siguiente
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                      <FileText className="w-5 h-5 mr-2 text-purple-600" />
                      Información de la Salida
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fecha de Salida*
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Calendar className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="date"
                            name="exit_date"
                            value={exitData.exit_date}
                            onChange={handleInputChange}
                            className={`pl-10 w-full py-2 border ${
                              errors.exit_date ? 'border-red-500' : 'border-gray-300'
                            } rounded-lg focus:ring-purple-500 focus:border-purple-500 text-gray-700 font-medium`}
                            required
                            style={{
                              backgroundImage: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)',
                              boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
                              cursor: 'pointer'
                            }}
                          />
                        </div>
                        {errors.exit_date && (
                          <p className="mt-1 text-sm text-red-600">{errors.exit_date}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Número de Requisición
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Tag className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="requisition_number"
                            value={exitData.requisition_number}
                            onChange={handleInputChange}
                            placeholder="Ej. REQ-001"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Entregado a
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <User className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="delivered_to"
                            value={exitData.delivered_to}
                            onChange={handleInputChange}
                            placeholder="Nombre de la persona"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div ref={truckInputRef} className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Camión
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Truck className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="truck"
                            value={exitData.truck}
                            onChange={handleInputChange}
                            placeholder="Ej. CR-25"
                            autoComplete="off"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                          {showTruckSuggestions && (
                            <ul className="absolute z-20 top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-60 overflow-auto divide-y divide-gray-100">
                              {truckSuggestions.map(t => (
                                <li
                                  key={t.id || t.numero_economico}
                                  onClick={() => handleSelectTruck(t.numero_economico)}
                                  className="px-3 py-2 cursor-pointer hover:bg-purple-50 flex items-center justify-between"
                                >
                                  <span className="text-sm font-medium text-gray-700">{t.numero_economico}</span>
                                  <span className="text-xs text-gray-500">{t.placas || t.serie || ''}</span>
                                </li>
                              ))}
                              {truckSuggestions.length === 0 && (
                                <li className="px-3 py-2 text-sm text-gray-500">Sin coincidencias</li>
                              )}
                            </ul>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">Escribe para buscar y selecciona la unidad.</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Taller
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Wrench className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="workshop"
                            value={exitData.workshop}
                            onChange={handleInputChange}
                            placeholder="Nombre del taller"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Kilometraje Actual
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Tag className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="number"
                            name="kilometraje"
                            value={exitData.kilometraje}
                            onChange={handleInputChange}
                            placeholder="Ej. 125000"
                            min="0"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Empresa
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Building2 className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="company"
                            value={exitData.company}
                            onChange={handleInputChange}
                            placeholder="Nombre de la empresa"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Solicitante
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <User className="w-5 h-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="solicitante"
                            value={exitData.solicitante}
                            onChange={handleInputChange}
                            placeholder="Nombre del solicitante"
                            className="pl-10 w-full py-2 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Motivo
                        </label>
                        <div className="relative">
                          <textarea
                            name="reason"
                            value={exitData.reason}
                            onChange={handleInputChange}
                            placeholder="Motivo de la salida"
                            rows="2"
                            className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Notas Adicionales
                        </label>
                        <div className="relative">
                          <textarea
                            name="notes"
                            value={exitData.notes}
                            onChange={handleInputChange}
                            placeholder="Notas o comentarios adicionales"
                            rows="3"
                            className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
                          />
                        </div>
                      </div>
                      
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Imagen de Requisición
                        </label>
                        <div 
                          className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setShowImageUploader(true)}
                        >
                          {exitData.requisition_image ? (
                            <div className="relative">
                              <img 
                                src={exitData.requisition_image_url || imagesService.getImageUrl(exitData.requisition_image)}
                                alt="Requisición" 
                                className="max-h-40 mx-auto rounded-lg"
                                onError={(e) => {
                                  console.error('Error cargando imagen de requisición:', e);
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyLjIwMDcgMTAuNjY2N0w4Ljg2NzM3IDcuMzMzMzRMMTIuMjAwNyA0TTMuNzM0MDQgNUgxMC4wNjc0TTMuNzM0MDQgMTBIMTAuMDY3NCIgc3Ryb2tlPSIjOTk5OTk5IiBzdHJva2Utd2lkdGg9IjEuMzMzMzMiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
                                  e.target.classList.add('p-8', 'bg-gray-100');
                                }}
                              />
                              <p className="mt-2 text-sm text-gray-600">Haz clic para cambiar la imagen</p>
                            </div>
                          ) : (
                            <div className="py-4">
                              <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                                <Upload className="w-6 h-6 text-purple-600" />
                              </div>
                              <p className="mt-2 text-sm text-gray-600">
                                Haz clic para subir una imagen de la requisición
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-6">
                    <motion.button
                      type="button"
                      onClick={prevStep}
                      className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Anterior
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={nextStep}
                      className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Siguiente
                    </motion.button>
                  </div>
                </motion.div>
              )}

              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                >
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                      <Check className="w-5 h-5 mr-2 text-purple-600" />
                      Confirmar Salida
                    </h3>
                    
                    {errors.submit && (
                      <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2" />
                        {errors.submit}
                      </div>
                    )}
                    
                    <div className="bg-purple-50 p-5 rounded-xl mb-6">
                      <h4 className="font-semibold text-purple-800 mb-2 flex items-center">
                        <BarChart className="w-5 h-5 mr-2 text-purple-700" />
                        Resumen de la Salida
                      </h4>
                      
                      <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Fecha de Salida:</p>
                            <p className="font-medium">
                              {exitData.exit_date ? (() => {
                                const date = new Date(exitData.exit_date);
                                const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                                const localDate = new Date(date.getTime() + userTimezoneOffset);
                                return localDate.toLocaleDateString();
                              })() : 'No especificada'}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-500">Requisición:</p>
                            <p className="font-medium">
                              {exitData.requisition_number || 'No especificada'}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-500">Entregado a:</p>
                            <p className="font-medium">
                              {exitData.delivered_to || 'No especificado'}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-500">Camión:</p>
                            <p className="font-medium">
                              {exitData.truck || 'No especificado'}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-500">Total de Productos:</p>
                            <p className="font-medium">
                              {selectedProducts.length} tipos / {selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} unidades
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-500">Costo Total:</p>
                            <p className="font-medium text-purple-700">
                              {formatMoney(calculateTotalCost())}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <h4 className="font-medium text-gray-700">
                          Productos a Retirar del Almacén
                        </h4>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Producto
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Cantidad
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Costo Unitario
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Costo Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {selectedProducts.map((product, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                    {product.image ? (
                                      <img 
                                        src={product.image} 
                                        alt={product.name} 
                                        className="h-8 w-8 rounded-lg object-cover"
                                      />
                                    ) : (
                                      <Package className="h-4 w-4 text-gray-400" />
                                    )}
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                    <div className="text-xs text-gray-500">{product.sku}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm text-gray-900">
                                  {product.quantity} {product.unit || 'pzs'}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-center">
                                <span className="text-sm text-gray-900">
                                  {formatMoney(product.unit_cost)}
                                </span>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-right">
                                <span className="text-sm font-medium text-gray-900">
                                  {formatMoney(product.total_cost)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-gray-50">
                          <tr>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              Total
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900">
                              {selectedProducts.reduce((sum, p) => sum + p.quantity, 0)} unidades
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap"></td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-bold text-purple-700">
                              {formatMoney(calculateTotalCost())}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
                        <div>
                          <p className="text-sm text-yellow-800">
                            <strong>Importante:</strong> Al confirmar esta salida, los productos seleccionados serán retirados del inventario.
                            Esta acción afectará el stock disponible.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between mt-6">
                    <motion.button
                      type="button"
                      onClick={prevStep}
                      className="flex items-center gap-2 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Anterior
                    </motion.button>
                    <motion.button
                      type="submit"
                      className={`flex items-center gap-2 px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors ${
                        loading ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                      whileHover={!loading ? { scale: 1.05 } : {}}
                      whileTap={!loading ? { scale: 0.95 } : {}}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <Save className="w-5 h-5" />
                          {isEditMode ? 'Actualizar Salida' : 'Confirmar Salida'}
                        </>
                      )}
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </div>
      </motion.div>

      {/* Notificación de escaneo de código de barras */}
      <AnimatePresence>
        {barcodeNotification.show && (
          <motion.div
            className={`fixed bottom-4 right-4 w-96 min-w-[24rem] bg-white shadow-xl rounded-lg overflow-hidden z-50 border-l-4 ${
              barcodeNotification.type === 'success' ? 'border-green-500' :
              barcodeNotification.type === 'error' ? 'border-red-500' :
              barcodeNotification.type === 'warning' ? 'border-yellow-500' : 'border-blue-500'
            }`}
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {barcodeNotification.type === 'success' ? (
                    <Check className="h-6 w-6 text-green-500" />
                  ) : barcodeNotification.type === 'error' ? (
                    <AlertCircle className="h-6 w-6 text-red-500" />
                  ) : barcodeNotification.type === 'warning' ? (
                    <AlertCircle className="h-6 w-6 text-yellow-500" />
                  ) : (
                    <QrCode className="h-6 w-6 text-blue-500" />
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {barcodeNotification.message}
                  </p>
                  {barcodeNotification.details && (
                    <p className="text-xs text-gray-500 truncate">
                      {barcodeNotification.details}
                    </p>
                  )}
                </div>
                <div className="ml-2 flex-shrink-0">
                  <button
                    onClick={() => setBarcodeNotification(prev => ({ ...prev, show: false }))}
                    className="bg-white rounded-md inline-flex text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <span className="sr-only">Cerrar</span>
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selector de Productos */}
      <AnimatePresence>
        {showProductSelector && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex justify-between items-center bg-blue-600 text-white p-5 rounded-t-2xl">
                <h3 className="text-xl font-bold">
                  Seleccionar Productos
                </h3>
                <motion.button
                  className="p-2 rounded-full hover:bg-blue-700 transition-colors"
                  onClick={() => setShowProductSelector(false)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <X className="w-6 h-6" />
                </motion.button>
              </div>
              
              <div className="p-5 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por nombre, SKU o categoría"
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 outline-none"
                    ref={productSearchRef}
                  />
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2">
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-10">
                    <Package className="w-12 h-12 text-gray-300 mx-auto" />
                    <p className="mt-2 text-gray-500">
                      No se encontraron productos que coincidan con la búsqueda
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-2">
                    {filteredProducts.map((product) => {
                      const isSelected = selectedProducts.some(p => p.id === product.id);
                      
                      return (
                        <motion.div
                          key={product.id}
                          className={`border rounded-xl p-3 cursor-pointer hover:shadow-md transition-all ${
                            isSelected ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200'
                          }`}
                          onClick={() => !isSelected && handleProductSelect(product)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              {product.image ? (
                                <img 
                                  src={product.image} 
                                  alt={product.name} 
                                  className="w-14 h-14 rounded-lg object-cover"
                                />
                              ) : (
                                <Package className="w-7 h-7 text-gray-400" />
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                  {product.name}
                                </h4>
                                {isSelected && (
                                  <span className="bg-blue-500 text-white p-1 rounded-full">
                                    <Check className="w-3 h-3" />
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-xs text-gray-500 truncate mt-1">
                                SKU: {product.sku}
                              </p>
                              
                              <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center">
                                  <div className="bg-green-100 text-green-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                    Stock: {product.current_stock} {product.unit || 'pzs'}
                                  </div>
                                </div>
                                
                                <div className="text-sm font-medium text-purple-700">
                                  {formatMoney(product.average_cost || product.price || 0)}
                                </div>
                              </div>
                              
                              {/* Mostrar el costo promedio claramente */}
                              <div className="mt-1 text-xs text-gray-600">
                                <span className="font-semibold">Costo promedio:</span> {formatMoney(product.average_cost || 0)}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {filteredProducts.length} productos disponibles
                  </div>
                  <motion.button
                    onClick={() => setShowProductSelector(false)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cerrar
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de subida de imagen */}
      <AnimatePresence>
        {showImageUploader && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl p-6 max-w-md w-full mx-4"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <h3 className="text-xl font-bold text-gray-800 mb-4">Subir Imagen de Requisición</h3>
              <ImageUploader
                onImageUpload={handleImageUpload}
                currentImage={exitData.requisition_image}
                onCancel={() => setShowImageUploader(false)}
                type="requisition"
              />
              {imageLoading && (
                <div className="mt-4 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-sm text-gray-600 mt-2">Subiendo imagen...</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ExitFormMulti;
